import React from 'react'

import { useBulkAction } from '@/hooks/useBulkAction'
import { useToasts } from '@/hooks/useToasts'
import { Controller, useForm } from 'react-hook-form'

import {
  Box,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'

import { Button, FormTextareaControl } from '@/components/ui'
import {
  CreateBulkIncidentActionInput,
  IncidentActionType,
} from '@/graphql/generated/schemas'
import { BUTTON_PRESS, startTimer, trackEvent } from '@/utils/analytics'

import { ResolveToggle } from '../../incident-drawer/resolve/components/ResolveToggle'

interface BulkActionModalIProps {
  incidentIds: string[]
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface FormInputsIProps {
  bulkActionNote: string
  isReal: boolean
}

export const BulkActionModal = ({
  incidentIds,
  isOpen,
  onClose,
  onSuccess,
}: BulkActionModalIProps) => {
  const title = 'Bulk Resolve'
  const subtitle = 'Are you sure you want to bulk resolve these Incidents?'

  const { showSuccess, showError } = useToasts()
  const titleColor = 'brand.400'
  const subtitleColor = 'brand.500'

  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
    defaultValues: {
      bulkActionNote: '',
      isReal: true,
    },
  })

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = form

  const { submitBulkAction } = useBulkAction()

  const onSubmit = async (data: FormInputsIProps) => {
    if (!incidentIds || incidentIds.length === 0) {
      showError('No incidents selected for bulk action')
      return
    }

    try {
      const bulkResolveButtonAnalyticName = `${BUTTON_PRESS} ${title} - Resolve`
      startTimer(bulkResolveButtonAnalyticName)
      const realFlag: IncidentActionType = data.isReal
        ? IncidentActionType.SetRealToTrue
        : IncidentActionType.SetRealToFalse

      const params: CreateBulkIncidentActionInput = {
        message: data.bulkActionNote,
        types: [IncidentActionType.Resolved, realFlag],
        incidentIds: incidentIds,
      }

      await submitBulkAction(params)

      showSuccess(
        `Successfully resolved ${incidentIds.length} incident${
          incidentIds.length > 1 ? 's' : ''
        }`
      )
      trackEvent(bulkResolveButtonAnalyticName, {
        incidentCount: incidentIds.length,
        real: data.isReal,
        message: data.bulkActionNote,
      })
      onSuccess()
      onClose()
    } catch (error) {
      showError(error?.message ?? 'Failed to resolve incidents')
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      id='bulkActionModal'
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      scrollBehavior='outside'
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='white' rounded='20px'>
        <ModalCloseButton
          color='black'
          isDisabled={isSubmitting}
          opacity={0.4}
        />

        <Box px='10' py='6'>
          <Box>
            <Box
              color={titleColor}
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              lineHeight='1'
              mb='3'
            >
              {title}
            </Box>
            <Box
              color={subtitleColor}
              fontSize='17px'
              letterSpacing='-0.4px'
              mt='3'
            >
              {subtitle}
            </Box>
          </Box>
        </Box>

        <ModalBody px='10'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display='flex' flexDirection='column'>
              <Text fontWeight={600} paddingBottom={2}>
                Resolution note
              </Text>

              <FormTextareaControl
                errorMessage={errors.bulkActionNote?.message}
                id='bulkActionNote'
                isInvalid={!!errors.bulkActionNote}
                maxCharCount={1_000}
                placeholder='Enter resolution note (optional)'
                textareaRef={register()}
              />
            </Box>

            <HStack paddingTop={5}>
              <VStack alignItems='start'>
                <Text fontWeight={600}>Were these real Incidents?</Text>
                <Text fontSize='13px' fontStyle='italic' marginBottom='20px'>
                  False Incidents will be tracked for Noise Reduction Features
                  and removed from Program Analytics.
                </Text>
              </VStack>

              <Spacer />

              <Controller
                control={control}
                defaultValue={true}
                name='isReal'
                render={({ onChange, value }) => (
                  <ResolveToggle isReal={value} onChange={onChange} />
                )}
              />
            </HStack>
          </form>
        </ModalBody>

        <ModalFooter py='4'>
          <Button
            isDisabled={isSubmitting}
            mr='3'
            onClick={handleClose}
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            isLoading={isSubmitting}
            loadingText='Resolving...'
            onClick={handleSubmit(onSubmit)}
            type='submit'
          >
            Resolve
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
